import { useEffect, useMemo, useState } from 'react';
import {
  createLocalSetupIntent,
  createLocalStripeAccount,
  createLocalStripeAccountLink,
  fetchLocalPaymentsConfig,
  fetchLocalStripeAccount,
  removeLocalDefaultCard,
  saveLocalDefaultCard,
  showLocalStripeCustomer,
  updateLocalStripeAccount,
} from './api';
import { denormalisedResponseEntities } from './data';
import * as log from './log';

const DISABLED = Object.freeze({ savedCards: false, payoutAccount: false });

export const LOCAL_PAYMENTS_UNAVAILABLE = 'local-payments-config-unavailable';

const unavailable = cause =>
  Object.assign(new Error('Payment settings could not be loaded'), {
    code: LOCAL_PAYMENTS_UNAVAILABLE,
    cause,
  });

const parseConfig = c => {
  const valid = !!c && typeof c.savedCards === 'boolean' && typeof c.payoutAccount === 'boolean';
  const on = valid && (c.savedCards || c.payoutAccount);
  if (!valid || (on && !(typeof c.publishableKey === 'string' && c.publishableKey))) {
    throw unavailable(new Error('Unexpected /api/payments/config response'));
  }
  return on
    ? { savedCards: c.savedCards, payoutAccount: c.payoutAccount, publishableKey: c.publishableKey }
    : DISABLED;
};

const configByUser = new Map();

/** Forgets every user's answer, so the next lookup asks the server again. */
export const resetLocalPaymentsConfig = () => configByUser.clear();

/**
 * `{ savedCards, payoutAccount, publishableKey }` for a signed-in user; one lookup per user. Rejects
 * with `code: LOCAL_PAYMENTS_UNAVAILABLE` when the server's answer is unknown: callers must not
 * treat that as "disabled", which would send the user's cards or payout account to Sharetribe and
 * its Stripe key. A failure is kept until `resetLocalPaymentsConfig` (logout) or a page reload, so
 * the records loaded with the current user and later mutations agree on where they live.
 */
export const getLocalPaymentsConfig = userId => {
  if (typeof window === 'undefined' || !userId) return Promise.resolve(DISABLED);
  if (!configByUser.has(userId)) {
    const lookup = Promise.resolve()
      .then(() => fetchLocalPaymentsConfig(userId))
      .then(parseConfig, e => {
        throw unavailable(e);
      });
    lookup.catch(() => {});
    configByUser.set(userId, lookup);
  }
  return configByUser.get(userId);
};

export const isLocalPaymentsUnavailable = e => e?.code === LOCAL_PAYMENTS_UNAVAILABLE;

// The subset of the Sharetribe SDK the payment ducks call, served by our server.
const localPaymentsSdk = {
  stripeCustomer: {
    create: ({ stripePaymentMethodId }) => saveLocalDefaultCard(stripePaymentMethodId),
    addPaymentMethod: ({ stripePaymentMethodId }) => saveLocalDefaultCard(stripePaymentMethodId),
    deletePaymentMethod: () => removeLocalDefaultCard(),
  },
  stripeSetupIntents: {
    create: () => createLocalSetupIntent(),
  },
  stripeAccount: {
    create: params => createLocalStripeAccount(params),
    update: params => updateLocalStripeAccount(params),
    fetch: () => fetchLocalStripeAccount(),
  },
  stripeAccountLinks: {
    create: params => createLocalStripeAccountLink(params),
  },
};

const currentUserId = getState => getState().user?.currentUser?.id?.uuid;

/**
 * Our server when `feature` ('savedCards' | 'payoutAccount') is on for the signed-in user, the SDK
 * when our server says it is off. Rejects (no call is made) when that is unknown, including in the
 * browser before the current user is loaded.
 */
export const paymentsSdk = (feature, getState, sdk) => {
  const userId = currentUserId(getState);
  if (typeof window !== 'undefined' && !userId) {
    return Promise.reject(unavailable(new Error('No current user')));
  }
  return getLocalPaymentsConfig(userId).then(config => (config[feature] ? localPaymentsSdk : sdk));
};

/**
 * App config with the Stripe publishable key for `feature`: ours when it runs on our server for
 * this user (card setup, account and bank tokens must come from the Stripe account our server
 * uses), the app's when our server says it is off, and none while that is unknown (`ready` false;
 * `unavailable` once the lookup failed). Forms that create Stripe.js on mount should wait for
 * `ready`.
 */
export const useStripeConfigFor = (feature, userId, config) => {
  const [lookup, setLookup] = useState({ userId: null, key: null, failed: false });
  useEffect(() => {
    if (!userId) return undefined;
    let current = true;
    getLocalPaymentsConfig(userId).then(
      c => {
        if (current) setLookup({ userId, key: c[feature] ? c.publishableKey : null, failed: false });
      },
      e => {
        log.error(e, 'local-payments-config-failed');
        if (current) setLookup({ userId, key: null, failed: true });
      }
    );
    return () => {
      current = false;
    };
  }, [feature, userId]);
  const known = !!userId && lookup.userId === userId;
  const ready = known && !lookup.failed;
  const key = ready ? lookup.key || config.stripe?.publishableKey : null;
  const stripeConfig = useMemo(
    () =>
      key === config.stripe?.publishableKey
        ? config
        : { ...config, stripe: { ...config.stripe, publishableKey: key } },
    [config, key]
  );
  return { ready, unavailable: known && lookup.failed, config: stripeConfig };
};

const included = (include, name) =>
  (include || []).some(i => i === name || i.startsWith(`${name}.`));

const entityOf = response => denormalisedResponseEntities(response)[0] || null;

/**
 * Replaces the Sharetribe `stripeCustomer` / `stripeAccount` the current user was loaded with by
 * ours when that part runs on our server. If our server cannot answer, the user has none rather
 * than Sharetribe's (a Sharetribe account would be the wrong one to pay out to or to update). When
 * it is unknown which applies, the user has neither.
 */
export const withLocalPaymentRecords = (currentUser, include) => {
  const wantsCustomer = included(include, 'stripeCustomer');
  const wantsAccount = included(include, 'stripeAccount');
  if (!wantsCustomer && !wantsAccount) return Promise.resolve({ currentUser, localAccount: false });

  // null, not absent: the user reducer keeps relationships the new user object leaves out.
  const withoutRecords = (customer, account) => {
    const next = { ...currentUser };
    if (customer) next.stripeCustomer = null;
    if (account) next.stripeAccount = null;
    return next;
  };

  return getLocalPaymentsConfig(currentUser.id?.uuid).then(config => {
    const customer = wantsCustomer && config.savedCards;
    const account = wantsAccount && config.payoutAccount;
    if (!customer && !account) return { currentUser, localAccount: false };

    const next = withoutRecords(customer, account);
    const load = (fetch, key, code) =>
      fetch()
        .then(response => {
          next[key] = entityOf(response);
        })
        .catch(e => log.error(e, code));
    return Promise.all([
      customer
        ? load(showLocalStripeCustomer, 'stripeCustomer', 'local-stripe-customer-failed')
        : null,
      account
        ? load(
            () => fetchLocalStripeAccount({ details: false }),
            'stripeAccount',
            'local-stripe-account-failed'
          )
        : null,
    ]).then(() => ({ currentUser: next, localAccount: account }));
  }, e => {
    log.error(e, 'local-payments-config-failed');
    return { currentUser: withoutRecords(wantsCustomer, wantsAccount), localAccount: wantsAccount };
  });
};
